import { UserServiceClient } from 'fitbeat.api-clients';
import React from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator, AppState,
  AppStateStatus,
  Dimensions,
  Keyboard,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm, reset } from 'redux-form';

import { Button, ButtonSize, ButtonTypes } from '../../components';
import { getApiConfig } from '../../config';
import { workoutReviewed, submitAfterWorkoutRating } from '../../HomeWorkouts';
import { GoogleAnalytics } from '../../services/google-analytics';
import Theme from '../../theme';
import {
  BaseModal,
  Divider,
  FormError,
  Ratings,
  TextInput,
  toggleRateHomeWorkoutModal,
} from '../../ui';
import {
  IMessagingButtonsProps,
  MessagingButtons,
} from '../../ui/components/MessagingButtons';
import { AuthTokenBuilder } from '../../utils/authTokenBuilder';
import { Logger } from '../../utils/Logger';
import { defaultScale } from '../../utils/scaling';
import { build } from '../../config';
import { openLink } from '../../utils/linkUtils';

const isElite = build.variant.isElite;

interface IProps {
  isRateHomeWorkoutModalVisible: boolean;
  toggleRateHomeWorkoutModal: (b: boolean) => object;
  homeWorkoutToReviewId: string | null;
  userProfile: any;
  navigation: any;
  markNotificationsOfTypeAsRead: any;
  t: any;
  handleSubmit: any;
  reset: (formName: string) => any;
  workoutReviewed: () => object;
  submitAfterWorkoutRating: (rating: number, comment: string) => void;
  afterHomeWorkoutReviewRedirectToDashboard: boolean;
  message: undefined | string;
  workoutRating: number;
}

interface IState {
  isLoading: boolean;
  reviewSubmitted: boolean;
  facebookMessengerLink: string | null;
  whatsAppLink: string | null;
  fetchingLinkError: boolean;
  fetchingMessagingLinks: boolean;
  formSubmittedWithErrors: boolean;
  messengerButtonLoading: string | null;
}

enum Constants {
  RATING_GRADIENT = 0.0873362,
  RATING_CONSTANT = -59,
  DIVIDER_GRADIENT = 0.131004,
  DIVIDER_CONSTANT = -57,
}

class HomeWorkoutReviewModalComponent extends React.PureComponent<IProps, IState> {
  private googleAnalytics = new GoogleAnalytics();
  private userServiceClient = new UserServiceClient(
    getApiConfig(), fetch, null, new AuthTokenBuilder(), this.googleAnalytics);

  public constructor(props: IProps) {
    super(props);
    this.state = {
      isLoading: false,
      reviewSubmitted: false,
      facebookMessengerLink: null,
      whatsAppLink: null,
      fetchingLinkError: false,
      formSubmittedWithErrors: false,
      fetchingMessagingLinks: false,
      messengerButtonLoading: null,
    };
  }

  public async componentDidMount() {
    try {
      this.setState({ fetchingMessagingLinks: true });
      const messagingData = await Promise.all([
        this.userServiceClient.getUserMessengerPlatformLink('FacebookMessenger'),
        this.userServiceClient.getUserMessengerPlatformLink('WhatsApp'),
      ]);

      this.setState({
        facebookMessengerLink: messagingData[0].platformRedirectLink,
        whatsAppLink: messagingData[1].platformRedirectLink,
      });

    } catch (e) {
      this.setState({ fetchingLinkError: true });
    } finally {
      this.setState({ fetchingMessagingLinks: false });
    }
  }

  public render() {
    return (
      <>
        {this.state.reviewSubmitted ? !isElite && this.ratingThanks() : this.rateYourWorkout()}
      </>
    );
  }

  private ratingThanks = () => {
    const { t, isRateHomeWorkoutModalVisible } = this.props;
    const messagingData: IMessagingButtonsProps = {
      openMessagingApp: this.openMessagingAppCloseModal,
      facebookMessengerLink: this.state.facebookMessengerLink,
      whatsAppLink: this.state.whatsAppLink,
      disabled: false,
      buttonLoading: this.state.messengerButtonLoading,
    };
    return (
      <BaseModal
        title={t('rateYourWorkout')}
        headerTextStyle={styles.headerTextStyle}
        isModalVisible={isRateHomeWorkoutModalVisible}
        closeModal={this.closeModal}
      >
        <ScrollView contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}>
          <Text style={styles.heading}>{t('ratingThanks')}</Text>
          {!isElite && <MessagingButtons {...messagingData}/>}
        </ScrollView>
        <Button
          title={t('close')}
          fontColor={Theme.PRIMARY}
          buttonType={ButtonTypes.TRANSPARENT}
          onPress={this.closeModal}
        />
      </BaseModal>
    );
  }

  private messagingButtonContent = () => {
    const messagingData: IMessagingButtonsProps = {
      openMessagingApp: this.openMessagingAppCloseModal,
      facebookMessengerLink: this.state.facebookMessengerLink,
      whatsAppLink: this.state.whatsAppLink,
      disabled: this.state.formSubmittedWithErrors,
      buttonLoading: this.state.messengerButtonLoading,
    };
    const {
      fetchingMessagingLinks,
      fetchingLinkError,
      facebookMessengerLink,
      whatsAppLink,
    } = this.state;
    const { message, workoutRating } = this.props;
    const showLoading = fetchingMessagingLinks && !fetchingLinkError;
    const showMessagingButtons = (facebookMessengerLink || whatsAppLink) && !fetchingLinkError && !message && !workoutRating;
    if (showLoading) {
      return (
        <ActivityIndicator
          size='small'
          color={Theme.PRIMARY}
          animating={true}
          style={styles.loadingIndicator}
        />
      );
    } else if (showMessagingButtons) {
      return (
        <>
          <Divider
            styles={{
              paddingVertical: this.calculatePadding(Constants.DIVIDER_GRADIENT, Constants.DIVIDER_CONSTANT),
            }}
            text={this.props.t('or')}/>
          <MessagingButtons {...messagingData}/>
        </>
      );
    }
  }

  private rateYourWorkout = () => {
    const {
      t,
      handleSubmit,
      isRateHomeWorkoutModalVisible,
    } = this.props;
    return (
      <BaseModal
        title={t('rateYourWorkout')}
        headerTextStyle={styles.headerTextStyle}
        isModalVisible={isRateHomeWorkoutModalVisible}
        closeModal={this.closeModal}
      >
        <ScrollView>
          <View
            style={{
              paddingTop: this.calculatePadding(Constants.RATING_GRADIENT, Constants.RATING_CONSTANT),
            }}>
            <Field
              name='rating'
              component={Ratings}
              size={defaultScale(55)}
              lightBackground={true}
            />
            <Field
              multiline
              isRequired={false}
              name='message'
              maxLength={defaultScale(1000)}
              numberOfLines={4}
              returnKeyLabel='return'
              component={TextInput}
              autoCapitalize='none'
              keyboardType='default'
              textInputStyle={{ color: Theme.BLACK }}
              inputContainerStyle={{ backgroundColor: Theme.GREY_LIGHT }}
              placeholder={t('leaveACommentModal')}
              showLabel={this.showPlaceHolderLabel()}
            />
            <View style={{ marginTop: defaultScale(3) }}>
              {this.state.formSubmittedWithErrors && !this.props.workoutRating &&
              <FormError error={{ message: t('pleaseSelectRating') }}/>}
              <Button
                title={t('submitRatingModal')}
                isLoading={this.state.isLoading}
                size={ButtonSize.MEDIUM}
                fontColor={Theme.WHITE}
                buttonType={ButtonTypes.NORMAL}
                onPress={handleSubmit(this.submit)}
              />
            </View>
          </View>

          {!isElite && this.messagingButtonContent()}
        </ScrollView>
      </BaseModal>
    );
  }

  private calculatePadding = (gradient: number, constant: number): number => {
    const height = Dimensions.get('screen').height;
    const padding = gradient * height + constant;
    if (padding < 0) {
      return 0;
    }
    return padding;
  }

  private closeModal = () => {
    this.props.toggleRateHomeWorkoutModal(false);
    this.props.reset('UserReviewFormModal');
    if (this.props.afterHomeWorkoutReviewRedirectToDashboard) {
      this.navigateDashboard();
    }
    this.props.workoutReviewed();
  }

  private handleReturnToAppFromMessaging = (status: AppStateStatus) => {
    if (status === 'active') {
      this.setState({ messengerButtonLoading: null });
      this.closeModal();
      this.setState({ reviewSubmitted: false });
      AppState.removeEventListener('change', this.handleReturnToAppFromMessaging);
    }
  }

  private openMessagingAppCloseModal = async (link: string): Promise<void> => {
    try {
      await openLink(link);
      this.setState({ messengerButtonLoading: link });
      AppState.addEventListener('change', this.handleReturnToAppFromMessaging);
    } catch (e) {
      this.handleError(e, `Failed to open messaging app ${link}`);
    }
  }

  private showPlaceHolderLabel = () => Platform.OS !== 'web';

  private navigateDashboard = () => {
    const {
      navigation,
    } = this.props;
    if (Platform.OS === 'web') {
      navigation.navigate('SummaryDashboard');
    } else {
      navigation.navigate('SummaryDashboard');
    }
  }

  private handleError = (error, caller = '') => {
    const { userProfile } = this.props;
    Logger.notify(error, (report) => {
      report.errorClass = 'HomeWorkoutReviewModal';
      const userDetails = `User id :${userProfile.uid}`;
      report.errorMessage = `Caller: ${caller}\n\nuserDetails: ${userDetails}`;
    });
  }

  private submit = async ({ message, rating }) => {
    const {
      toggleRateHomeWorkoutModal: toggleModal,
      homeWorkoutToReviewId,
      reset: resetForm,
      submitAfterWorkoutRating,
    } = this.props;
    const invalidSubmit = !rating;
    if (invalidSubmit) {
      this.setState({ formSubmittedWithErrors: true });
      return;
    }

    try {
      Keyboard.dismiss();
      await this.setState({ isLoading: true });
      if (homeWorkoutToReviewId) {
        submitAfterWorkoutRating(rating, message);
        await this.userServiceClient.reviewWorkout(homeWorkoutToReviewId, rating, message);
      }
      await this.setState({ reviewSubmitted: true });
    } catch (error) {
      this.handleError(error, 'addReview');
      toggleModal(false);
    } finally {
      await this.setState({ isLoading: false });
      resetForm('UserReviewFormModal');
    }
  }
}

const styles = StyleSheet.create({
  headerTextStyle: {
    zIndex: 1,
    paddingBottom: defaultScale(10),
    borderBottomWidth: 2,
    borderBottomColor: Theme.GREY_LIGHT,
  },
  loadingIndicator: {
    flex: 1,
    justifyContent: 'center',
    paddingTop: defaultScale(50),
  },
  modalContent: {
    flex: 1,
    justifyContent: 'space-between',
  },
  hr: {
    borderBottomColor: Theme.GREY_LIGHT,
    borderBottomWidth: 2,
    marginBottom: defaultScale(20),
  },
  heading: {
    ...Theme.textStyle.heading,
    fontSize: defaultScale(28),
    paddingTop: defaultScale(30),
    paddingBottom: defaultScale(25),
    color: Theme.BLACK,
  },
});

const selector = formValueSelector('UserReviewFormModal');
const mapStateToProps = (state) => {
  // State.homeWorkouts is not available on web
  return {
    isRateHomeWorkoutModalVisible: state.homeWorkouts ? !!state.homeWorkouts.homeWorkoutToReviewId : false,
    homeWorkoutToReviewId: state.homeWorkouts ? state.homeWorkouts.homeWorkoutToReviewId : null,
    afterHomeWorkoutReviewRedirectToDashboard:
      state.homeWorkouts ? state.homeWorkouts.afterHomeWorkoutReviewRedirectToDashboard : true,
    message: selector(state, 'message'),
    workoutRating: selector(state, 'rating'),
  };
};

const
  ReviewModalTranslated = translate(['dashboard'], { wait: true })(HomeWorkoutReviewModalComponent);
const
  ReviewModalTranslatedReduxForm = reduxForm({
    form: 'UserReviewFormModal',
    destroyOnUnmount: false,
  })(ReviewModalTranslated);

export const HomeWorkoutReviewModal = connect(mapStateToProps, {
  toggleRateHomeWorkoutModal,
  workoutReviewed,
  submitAfterWorkoutRating,
  reset,
})
(
  ReviewModalTranslatedReduxForm,
);
